import { createTheme, ThemeProvider } from '@mui/material'
import { useEffect, useLayoutEffect, useState } from 'react'
import ReactGA from 'react-ga4'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import MediaQuery from 'react-responsive'
import { Route, Switch, useLocation } from 'react-router-dom'

import { getFacets } from 'store/actions/facets'
import { getFrontendSettings } from 'store/actions/frontendSettings'
import { getPipelineStages } from 'store/actions/pipelineStages'
import {
  checkIpRange,
  getCurrentUser,
  keepSessionAlive,
  updateSessionStatus
} from 'store/actions/user'
import { selectHasExecutePermissions, selectUser } from 'store/selectors/user'
import {
  BASE_PAGE,
  SEARCH_PAGE,
  PROFILE_PAGE,
  PIPELINE_PAGE,
  GIFT_PAGE,
  CONTACT_PAGE,
  LOGIN,
  USER_PROFILE,
  RESET_PASSWORD,
  OAUTH_AUTHORIZE
} from 'utils/paths'

import AuthRoute from 'components/AuthRoute'
import Banner from 'components/global/banner'
import LeftMenu from 'components/global/drawer/LeftMenu'
import HeaderBar from 'components/global/header/HeaderBar'
import StatusCard from 'components/global/status'
import SearchForm from 'components/SearchForm'
import SessionTimeoutModal from 'components/SessionTimeoutModal'

import { BaseRedirect } from 'pages'
import ContactPage from 'pages/contact'
import GiftPage from 'pages/gift'
import Login from 'pages/login/Login'
import OAuthAuthorizePage from 'pages/oauth_authorize'
import PasswordResetPage from 'pages/password'
import PipelinePage from 'pages/pipeline'
import ProfilePage from 'pages/profile'
import SearchPage from 'pages/search'
import UserProfilePage from 'pages/user_profile'

ReactGA.initialize('G-0PJB5KMFL2')

const theme = createTheme({})

const App = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { i18n } = useTranslation()

  const [sidebarOpen, setSidebarOpen] = useState(false)

  const { isAuthenticated } = useSelector(selectUser)
  const hasExecutePermissions = useSelector(selectHasExecutePermissions)

  useEffect(() => {
    // Start interval to ping API to keep session alive
    dispatch(keepSessionAlive())

    // Check if user is authenticated based on IP range
    if (!isAuthenticated) {
      if (location.pathname !== RESET_PASSWORD) {
        dispatch(checkIpRange())
      }
    } else {
      dispatch(getCurrentUser())
      dispatch(updateSessionStatus())
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getFrontendSettings())
      dispatch(getFacets())

      if (hasExecutePermissions) {
        dispatch(getPipelineStages())
      }
    }
  }, [isAuthenticated])

  useLayoutEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  return (
    <ThemeProvider theme={theme}>
      <div className="App">
        <LeftMenu onCloseIconButtonClick={() => setSidebarOpen(false)} showMenu={sidebarOpen} />
        <header className="App-header">
          <HeaderBar onLeftIconButtonClick={() => setSidebarOpen(open => !open)} />
          {isAuthenticated && (
            <>
              <MediaQuery query="(min-width: 768px)">
                <SearchForm />
              </MediaQuery>
              <MediaQuery query="(max-width: 767px)">
                <SearchForm mobile={true} />
              </MediaQuery>
              <Banner />
            </>
          )}
        </header>

        <StatusCard />
        <SessionTimeoutModal />

        <Switch>
          <Route path={BASE_PAGE} exact component={BaseRedirect} />
          <Route path={LOGIN} exact component={Login} />
          <Route path={RESET_PASSWORD} exact component={PasswordResetPage} />
          <Route path={OAUTH_AUTHORIZE} exact component={OAuthAuthorizePage} />
          <AuthRoute path={SEARCH_PAGE} exact component={SearchPage} />
          <AuthRoute path={`${PROFILE_PAGE}/:profileId/:tab?/:extra*`} component={ProfilePage} />
          <AuthRoute path={`${USER_PROFILE}/:tab?`} component={UserProfilePage} />
          <AuthRoute path={PIPELINE_PAGE} exact component={PipelinePage} />
          <AuthRoute path={GIFT_PAGE} exact component={GiftPage} />
          <AuthRoute path={CONTACT_PAGE} exact component={ContactPage} />
          <Route path="*" component={BaseRedirect} />
        </Switch>
      </div>
    </ThemeProvider>
  )
}

export default App
